import React, { useState, useEffect, useCallback } from 'react';
import { Card, CardContent, CardHeader, CardTitle } from '../components/ui/card';
import { Button } from '../components/ui/button';
import { Input } from '../components/ui/input';
import { Label } from '../components/ui/label';
import { Badge } from '../components/ui/badge';
import { Separator } from '../components/ui/separator';
import { Tabs, TabsContent, TabsList, TabsTrigger } from '../components/ui/tabs';
import { Select, SelectContent, SelectItem, SelectTrigger, SelectValue } from '../components/ui/select';
import { ScrollArea } from '../components/ui/scroll-area';
import { Progress } from '../components/ui/progress';
import {
  TrendingUp, Building2, UtensilsCrossed, Search, ExternalLink,
  RefreshCw, DollarSign, BarChart3, Percent, Clock,
  Sparkles, ChevronRight, AlertTriangle, CheckCircle, History,
  MapPin, FileText, Shield, Award, Users, Ruler, Calendar
} from 'lucide-react';
import api from '../services/api';
import { toast } from 'sonner';
import { format, parseISO } from 'date-fns';

const fmtDate = (d) => { try { return d ? format(parseISO(d), 'MMM d, yyyy') : '-'; } catch { return '-'; } };
const fmtMoney = (n) => n != null ? `$${Number(n).toLocaleString('en-US', { maximumFractionDigits: 0 })}` : 'N/A';
const fmtPct = (n) => n != null ? `${Number(n).toFixed(1)}%` : 'N/A';

const CONFIDENCE_STYLES = {
  high:   'bg-green-50 border-green-200 text-green-700',
  medium: 'bg-amber-50 border-amber-200 text-amber-700',
  low:    'bg-red-50 border-red-200 text-red-700',
};

const VIABILITY_COLOR = (s) => s >= 8 ? 'text-green-600' : s >= 5 ? 'text-amber-600' : 'text-red-600';

const VILLAGES = ['Southampton','East Hampton','Bridgehampton','Montauk','Sag Harbor','Amagansett','Wainscott','Water Mill','Quogue','Hampton Bays','Shelter Island'];

/* ── Small helper components ── */
function MetricCard({ label, value, sub, icon: Icon, color = 'text-primary', highlight = false }) {
  return (
    <div className={`p-4 rounded-xl border ${highlight ? 'bg-primary/5 border-primary/20' : 'bg-muted/40 border-border'}`}>
      <div className="flex items-center justify-between mb-1">
        <span className="text-xs font-semibold uppercase tracking-wide text-muted-foreground">{label}</span>
        {Icon && <Icon size={14} className={color} />}
      </div>
      <div className={`text-2xl font-bold tabular-nums ${color}`} style={{ fontFamily: 'Cormorant Garamond, serif' }}>{value}</div>
      {sub && <div className="text-xs text-muted-foreground mt-0.5">{sub}</div>}
    </div>
  );
}

function CostBar({ label, pct, color }) {
  return (
    <div className="space-y-1">
      <div className="flex justify-between text-xs">
        <span className="text-muted-foreground">{label}</span>
        <span className="font-semibold tabular-nums">{fmtPct(pct)}</span>
      </div>
      <div className="w-full bg-muted rounded-full h-2">
        <div className={`h-2 rounded-full ${color}`} style={{ width: `${Math.min(pct || 0, 100)}%` }} />
      </div>
    </div>
  );
}

/* ── Certificate of Occupancy Visual Component ── */
function CertificateOfOccupancy({ result, citations, onUseSeatCount }) {
  if (!result) return null;

  const confidenceColor = result.data_confidence === 'high' ? '#1F7A5A' : result.data_confidence === 'medium' ? '#B7791F' : '#B42318';
  const seatConfColor   = result.seat_confidence  === 'high' ? '#1F7A5A' : result.seat_confidence  === 'medium' ? '#B7791F' : '#B42318';
  const coFound = result.co_found && result.max_occupancy;

  return (
    <div className="space-y-4 fade-in">
      {/* CO Certificate Card */}
      <div className="rounded-2xl overflow-hidden shadow-lg border border-[#0D2A4A]/20">
        {/* Header */}
        <div className="bg-[#0D2A4A] px-6 py-4 flex items-center justify-between">
          <div className="flex items-center gap-3">
            <div className="w-10 h-10 rounded-full bg-white/10 border-2 border-white/30 flex items-center justify-center">
              <Shield size={20} className="text-white" />
            </div>
            <div>
              <div className="text-white font-bold text-sm tracking-widest uppercase">Suffolk County, New York</div>
              <div className="text-white/70 text-xs tracking-wide">
                {coFound ? 'Department of Buildings — Certificate of Occupancy' : 'Building Records Search — Capacity Analysis'}
              </div>
            </div>
          </div>
          <div className="text-right">
            {result.certificate_number
              ? <><div className="text-white/60 text-xs">Certificate No.</div><div className="text-white font-bold font-mono text-sm">{result.certificate_number}</div></>
              : <div className={`px-3 py-1.5 rounded-full text-xs font-bold border ${coFound ? 'bg-green-500/20 border-green-400/40 text-green-300' : 'bg-amber-500/20 border-amber-400/40 text-amber-300'}`}>
                  {coFound ? '✓ CO Found' : 'Capacity Estimated'}
                </div>
            }
          </div>
        </div>

        {/* Body */}
        <div className="bg-[#F8F6F0] px-6 py-5">
          <div className="border-2 border-[#C9A46A]/40 rounded-xl p-5 space-y-5">

            {/* Address + owner */}
            <div className="flex items-start justify-between">
              <div>
                <div className="text-xs font-bold uppercase tracking-widest text-[#C9A46A]">Premises</div>
                <div className="text-lg font-bold text-[#0D2A4A] mt-0.5" style={{ fontFamily: 'Cormorant Garamond, serif' }}>{result.address}</div>
                {result.owner_of_record && <div className="text-sm text-muted-foreground mt-0.5">Owner: {result.owner_of_record}</div>}
              </div>
              {result.issue_date && (
                <div className="text-right">
                  <div className="text-xs font-bold uppercase tracking-widest text-[#C9A46A]">Issued</div>
                  <div className="text-sm font-semibold text-[#0D2A4A]">{result.issue_date}</div>
                </div>
              )}
            </div>

            {/* Only show grid if we have data */}
            {[result.approved_use, result.occupancy_type, result.zoning_district, result.year_built, result.building_sqft, result.floors].some(Boolean) && (
              <>
                <Separator className="border-[#C9A46A]/30" />
                <div className="grid grid-cols-2 sm:grid-cols-3 gap-3">
                  {[
                    { label: 'Approved Use',    value: result.approved_use,    icon: FileText },
                    { label: 'Occupancy Type',  value: result.occupancy_type,  icon: Award },
                    { label: 'Zoning',          value: result.zoning_district, icon: MapPin },
                    { label: 'Year Built',      value: result.year_built,      icon: Calendar },
                    { label: 'Building Sq Ft',  value: result.building_sqft ? `${result.building_sqft.toLocaleString()} sqft` : null, icon: Ruler },
                    { label: 'Floors',          value: result.floors ? `${result.floors} floor${result.floors !== 1 ? 's' : ''}` : null, icon: Building2 },
                  ].filter(f => f.value).map(field => (
                    <div key={field.label} className="bg-white rounded-lg border border-[#C9A46A]/20 p-3">
                      <div className="flex items-center gap-1.5 mb-1"><field.icon size={11} className="text-[#C9A46A]" /><span className="text-xs font-bold uppercase tracking-wide text-[#C9A46A]">{field.label}</span></div>
                      <div className="text-sm font-semibold text-[#0D2A4A]">{field.value}</div>
                    </div>
                  ))}
                </div>
              </>
            )}

            <Separator className="border-[#C9A46A]/30" />

            {/* MAX OCCUPANCY hero — or estimated capacity */}
            <div className="bg-[#0D2A4A] rounded-xl p-4 flex items-center justify-between">
              <div className="flex items-center gap-3">
                <div className="w-12 h-12 rounded-full bg-white/10 border-2 border-[#C9A46A]/50 flex items-center justify-center">
                  <Users size={22} className="text-[#C9A46A]" />
                </div>
                <div>
                  <div className="text-[#C9A46A] text-xs font-bold uppercase tracking-widest">
                    {coFound ? 'Maximum Occupancy Load' : 'Estimated Capacity'}
                  </div>
                  <div className="text-white text-4xl font-bold tabular-nums mt-0.5" style={{ fontFamily: 'Cormorant Garamond, serif' }}>
                    {result.max_occupancy ?? result.estimated_seats ?? '—'}
                  </div>
                  <div className="text-white/60 text-xs mt-0.5">
                    {coFound ? 'Total persons permitted per CO' : 'From public records & listings'}
                  </div>
                </div>
              </div>
              <div className="text-right">
                <div className="text-[#C9A46A]/70 text-xs font-bold uppercase">Confidence</div>
                <div className="mt-1 px-3 py-1 rounded-full text-xs font-bold border" style={{ backgroundColor: `${confidenceColor}20`, borderColor: `${confidenceColor}40`, color: confidenceColor }}>
                  {result.data_confidence?.toUpperCase()}
                </div>
              </div>
            </div>

            {/* No CO warning */}
            {!coFound && (
              <div className="flex items-start gap-2 p-3 rounded-lg bg-amber-50 border border-amber-200">
                <AlertTriangle size={13} className="text-amber-600 mt-0.5 flex-shrink-0" />
                <p className="text-xs text-amber-700">
                  Official CO not found in public search results. Seat estimate is based on {result.data_sources_found?.length > 0 ? result.data_sources_found.slice(0,3).join(', ') : 'available public data'}.
                  For official records, contact East Hampton or Southampton Town Building Department directly.
                </p>
              </div>
            )}
          </div>
        </div>
      </div>

      {/* Seat Count Calculation Card */}
      <Card className="border-2 border-primary/20 bg-primary/5">
        <CardHeader className="pb-3">
          <CardTitle className="text-base flex items-center gap-2" style={{ fontFamily: 'Cormorant Garamond, serif' }}>
            <UtensilsCrossed size={16} className="text-primary" />
            Calculated Seat Count
          </CardTitle>
        </CardHeader>
        <CardContent className="space-y-4">
          <div className="flex items-center justify-between p-4 bg-white rounded-xl border border-primary/20">
            <div>
              <div className="text-xs font-bold uppercase tracking-widest text-primary">Estimated Dining Seats</div>
              <div className="text-5xl font-bold text-[#0D2A4A] mt-1 tabular-nums" style={{ fontFamily: 'Cormorant Garamond, serif' }}>
                {result.estimated_seats ?? '—'}
              </div>
              {result.seat_calculation_method && <div className="text-xs text-muted-foreground mt-1 max-w-xs leading-relaxed">{result.seat_calculation_method}</div>}
            </div>
            <div className="text-right space-y-2">
              <div className="px-3 py-1.5 rounded-full text-xs font-bold border" style={{ backgroundColor: `${seatConfColor}15`, borderColor: `${seatConfColor}30`, color: seatConfColor }}>
                {result.seat_confidence?.toUpperCase()} CONFIDENCE
              </div>
              {result.seat_confidence_reason && <div className="text-xs text-muted-foreground max-w-[160px] text-right leading-relaxed">{result.seat_confidence_reason}</div>}
            </div>
          </div>

          {/* Occupancy breakdown — only if we have max_occupancy */}
          {result.max_occupancy && (
            <div className="space-y-2">
              <div className="text-xs font-semibold uppercase tracking-wide text-muted-foreground">Occupancy Breakdown</div>
              {[
                { label: 'Max Occupancy (CO)', value: result.max_occupancy, pct: 100, color: 'bg-muted-foreground/30' },
                { label: 'Staff & Bar (~20%)',   value: Math.round(result.max_occupancy * 0.2),  pct: 20, color: 'bg-amber-400' },
                { label: 'Circulation (~15%)',   value: Math.round(result.max_occupancy * 0.15), pct: 15, color: 'bg-orange-400' },
                { label: 'Dining Seats (~65%)',  value: result.estimated_seats, pct: result.estimated_seats ? Math.round((result.estimated_seats / result.max_occupancy) * 100) : 65, color: 'bg-primary' },
              ].map(item => (
                <div key={item.label} className="flex items-center gap-3">
                  <div className="w-40 flex-shrink-0 text-xs text-muted-foreground text-right">{item.label}</div>
                  <div className="flex-1 bg-muted rounded-full h-3 overflow-hidden">
                    <div className={`h-3 rounded-full ${item.color} transition-all`} style={{ width: `${item.pct}%` }} />
                  </div>
                  <div className="w-10 text-xs font-bold tabular-nums">{item.value}</div>
                </div>
              ))}
            </div>
          )}

          {result.alternative_seat_sources?.length > 0 && (
            <div className="p-3 rounded-lg bg-muted/50 space-y-1">
              <div className="text-xs font-semibold text-muted-foreground">Other Capacity Data Found:</div>
              {result.alternative_seat_sources.map((s, i) => <div key={i} className="flex items-center gap-2 text-xs text-foreground"><CheckCircle size={10} className="text-green-500 flex-shrink-0" />{s}</div>)}
            </div>
          )}

          {result.estimated_seats && onUseSeatCount && (
            <Button onClick={() => onUseSeatCount(result.estimated_seats)} className="w-full bg-primary" data-testid="use-seat-count-btn">
              <UtensilsCrossed size={14} className="mr-1.5" />
              Use {result.estimated_seats} Seats in Capital Analysis
            </Button>
          )}
        </CardContent>
      </Card>

      {/* Sources */}
      {(result.notes || citations?.length > 0 || result.data_sources_found?.length > 0) && (
        <Card>
          <CardHeader className="pb-2"><CardTitle className="text-sm flex items-center gap-2"><ExternalLink size={13} />Record Sources</CardTitle></CardHeader>
          <CardContent className="space-y-2">
            {result.notes && <p className="text-xs text-muted-foreground italic">{result.notes}</p>}
            {result.data_sources_found?.length > 0 && <div className="space-y-1">{result.data_sources_found.map((s, i) => <div key={i} className="flex items-center gap-2 text-xs text-muted-foreground"><CheckCircle size={10} className="text-green-500 flex-shrink-0" />{s}</div>)}</div>}
            {citations?.length > 0 && (
              <div className="flex flex-wrap gap-1.5 pt-1 border-t border-border">
                {citations.map((url, i) => {
                  let domain = url; try { domain = new URL(url).hostname.replace('www.', ''); } catch {}
                  return <a key={i} href={url} target="_blank" rel="noopener noreferrer" className="flex items-center gap-1 px-2 py-1 rounded-full bg-muted hover:bg-secondary border border-border text-xs text-muted-foreground hover:text-foreground transition-colors"><ExternalLink size={9} />{domain}</a>;
                })}
              </div>
            )}
          </CardContent>
        </Card>
      )}
    </div>
  );
}


/* ── Restaurant Analysis Results ── */
function RestaurantAnalysis({ result, citations }) {
  if (!result) return null;
  if (result.parse_error) {
    return <Card><CardContent className="p-4"><p className="text-sm text-muted-foreground whitespace-pre-wrap">{result.raw_analysis}</p></CardContent></Card>;
  }
  const costs = result.cost_breakdown || {};
  const coVerifiedSeats = result.co_verified_seats;
  return (
    <div className="space-y-4 fade-in">
      <div className="flex items-start justify-between">
        <div>
          <h2 className="text-xl font-semibold" style={{ fontFamily: 'Cormorant Garamond, serif' }}>{result.restaurant_name}</h2>
          <p className="text-sm text-muted-foreground flex items-center gap-1 mt-0.5"><MapPin size={12} />{result.location}</p>
        </div>
        <div className="flex items-center gap-2">
          {coVerifiedSeats && (
            <div className="flex items-center gap-1.5 px-3 py-1.5 rounded-full text-xs font-semibold border bg-[#C9A46A]/10 border-[#C9A46A]/40 text-[#8B6914]">
              <Shield size={11} />
              CO-Verified: {coVerifiedSeats} seats
            </div>
          )}
          {result.revenue_confidence && (
            <div className={`flex items-center gap-1.5 px-3 py-1.5 rounded-full text-xs font-semibold border ${CONFIDENCE_STYLES[result.revenue_confidence] || CONFIDENCE_STYLES.medium}`}>
              {result.revenue_confidence === 'high' ? <CheckCircle size={11} /> : <AlertTriangle size={11} />}
              {result.revenue_confidence} confidence
            </div>
          )}
        </div>
      </div>
      <div className="grid grid-cols-2 sm:grid-cols-3 lg:grid-cols-4 gap-3">
        <MetricCard label="Peak Monthly Revenue" value={fmtMoney(result.estimated_monthly_revenue_peak)} sub="Jun–Sep season" icon={TrendingUp} color="text-primary" highlight />
        <MetricCard label="Annual Revenue" value={fmtMoney(result.estimated_annual_revenue)} sub="Full year estimate" icon={DollarSign} color="text-primary" highlight />
        <MetricCard label="Monthly Profit" value={fmtMoney(result.estimated_monthly_profit)} sub="Net after costs" icon={DollarSign} color={result.estimated_monthly_profit > 0 ? 'text-green-600' : 'text-red-600'} />
        <MetricCard label="Net Profit Margin" value={fmtPct(costs.net_profit_margin_pct)} sub="Industry benchmark" icon={Percent} color={costs.net_profit_margin_pct > 15 ? 'text-green-600' : costs.net_profit_margin_pct > 8 ? 'text-amber-600' : 'text-red-600'} />
        <MetricCard label="Est. Seats" value={coVerifiedSeats ? `${coVerifiedSeats} ✓` : (result.estimated_seat_count ?? 'N/A')} sub={coVerifiedSeats ? 'CO-verified' : 'Capacity'} icon={UtensilsCrossed} color={coVerifiedSeats ? 'text-[#8B6914]' : 'text-foreground'} />
        <MetricCard label="Avg Check" value={fmtMoney(result.estimated_avg_check)} sub="Per person" icon={DollarSign} />
        <MetricCard label="Turns/Night" value={result.estimated_turns_per_night ?? 'N/A'} sub="Table turns" icon={Clock} />
        <MetricCard label="Revenue/Seat/Year" value={fmtMoney(result.revenue_per_seat_annual)} sub="Efficiency metric" icon={BarChart3} />
      </div>
      {Object.keys(costs).length > 0 && (
        <Card>
          <CardHeader className="pb-3"><CardTitle className="text-base" style={{ fontFamily: 'Cormorant Garamond, serif' }}>Cost Structure Breakdown</CardTitle></CardHeader>
          <CardContent>
            <div className="grid grid-cols-1 sm:grid-cols-2 gap-4">
              <div className="space-y-3">
                <CostBar label="Food & Beverage Cost" pct={costs.food_cost_pct} color="bg-amber-400" />
                <CostBar label="Labor Cost" pct={costs.labor_cost_pct} color="bg-blue-400" />
                <CostBar label="Occupancy / Rent" pct={costs.occupancy_cost_pct} color="bg-purple-400" />
                <CostBar label="Other Operating Costs" pct={costs.other_costs_pct} color="bg-gray-400" />
                <CostBar label="Net Profit" pct={costs.net_profit_margin_pct} color="bg-green-500" />
              </div>
              <div className="space-y-2">
                {[
                  { label: 'Food Cost', pct: costs.food_cost_pct, monthly: (result.estimated_monthly_revenue_peak || 0) * ((costs.food_cost_pct || 0) / 100) },
                  { label: 'Labor', pct: costs.labor_cost_pct, monthly: (result.estimated_monthly_revenue_peak || 0) * ((costs.labor_cost_pct || 0) / 100) },
                  { label: 'Occupancy', pct: costs.occupancy_cost_pct, monthly: (result.estimated_monthly_revenue_peak || 0) * ((costs.occupancy_cost_pct || 0) / 100) },
                  { label: 'Other', pct: costs.other_costs_pct, monthly: (result.estimated_monthly_revenue_peak || 0) * ((costs.other_costs_pct || 0) / 100) },
                ].map(item => (
                  <div key={item.label} className="flex justify-between text-sm py-1 border-b border-border last:border-0">
                    <span className="text-muted-foreground">{item.label}</span>
                    <span className="font-semibold tabular-nums">{fmtMoney(item.monthly)}<span className="text-xs text-muted-foreground ml-1">/ mo</span></span>
                  </div>
                ))}
              </div>
            </div>
          </CardContent>
        </Card>
      )}
      {result.key_intelligence && (
        <Card className="bg-primary/5 border-primary/20">
          <CardHeader className="pb-2"><CardTitle className="text-base flex items-center gap-2" style={{ fontFamily: 'Cormorant Garamond, serif' }}><Sparkles size={15} className="text-primary" />Key Intelligence</CardTitle></CardHeader>
          <CardContent><p className="text-sm text-foreground leading-relaxed">{result.key_intelligence}</p></CardContent>
        </Card>
      )}
      <div className="grid grid-cols-1 sm:grid-cols-2 gap-4">
        {result.investment_outlook && <Card><CardHeader className="pb-2"><CardTitle className="text-sm">Investment Outlook</CardTitle></CardHeader><CardContent><p className="text-sm text-muted-foreground">{result.investment_outlook}</p></CardContent></Card>}
        {result.seasonal_notes && <Card><CardHeader className="pb-2"><CardTitle className="text-sm">Seasonal Analysis</CardTitle></CardHeader><CardContent><p className="text-sm text-muted-foreground">{result.seasonal_notes}</p></CardContent></Card>}
      </div>
      {result.comparable_restaurants?.length > 0 && (
        <Card>
          <CardHeader className="pb-2"><CardTitle className="text-base" style={{ fontFamily: 'Cormorant Garamond, serif' }}>Comparable Restaurants</CardTitle></CardHeader>
          <CardContent><div className="space-y-2">{result.comparable_restaurants.map((c, i) => (
            <div key={i} className="flex items-center justify-between p-2.5 rounded-lg bg-muted/40">
              <div><div className="text-sm font-medium">{c.name}</div><div className="text-xs text-muted-foreground">{c.similarity}</div></div>
              <div className="text-sm font-semibold text-primary tabular-nums">{fmtMoney(c.estimated_revenue)}<span className="text-xs text-muted-foreground">/yr</span></div>
            </div>
          ))}</div></CardContent>
        </Card>
      )}
      {citations?.length > 0 && (
        <Card><CardHeader className="pb-2"><CardTitle className="text-sm">Sources</CardTitle></CardHeader>
          <CardContent><div className="flex flex-wrap gap-1.5">{citations.map((url, i) => {
            let domain = url; try { domain = new URL(url).hostname.replace('www.', ''); } catch {}
            return <a key={i} href={url} target="_blank" rel="noopener noreferrer" className="flex items-center gap-1 px-2 py-1 rounded-full bg-muted border border-border text-xs text-muted-foreground hover:text-foreground"><ExternalLink size={9} />{domain}</a>;
          })}</div></CardContent>
        </Card>
      )}
    </div>
  );
}

/* ── Real Estate Analysis ── */
function RealEstateAnalysis({ result, citations }) {
  if (!result) return null;
  if (result.parse_error) return <Card><CardContent className="p-4"><p className="text-sm text-muted-foreground whitespace-pre-wrap">{result.raw_analysis}</p></CardContent></Card>;
  const roi = result.roi_scenarios || {};
  const viability = result.restaurant_viability_score;
  return (
    <div className="space-y-4 fade-in">
      <div className="flex items-start justify-between">
        <div>
          <h2 className="text-xl font-semibold" style={{ fontFamily: 'Cormorant Garamond, serif' }}>{result.property_name}</h2>
          <p className="text-sm text-muted-foreground flex items-center gap-1 mt-0.5"><MapPin size={12} />{result.location}</p>
        </div>
        <div className="flex items-center gap-2">
          {viability && <div className="text-center px-3 py-1.5 rounded-xl border bg-muted"><div className={`text-2xl font-bold tabular-nums ${VIABILITY_COLOR(viability)}`}>{viability}<span className="text-sm">/10</span></div><div className="text-xs text-muted-foreground">Restaurant Viability</div></div>}
          {result.data_confidence && <div className={`flex items-center gap-1.5 px-3 py-1.5 rounded-full text-xs font-semibold border ${CONFIDENCE_STYLES[result.data_confidence] || CONFIDENCE_STYLES.medium}`}>{result.data_confidence} confidence</div>}
        </div>
      </div>
      <div className="grid grid-cols-2 sm:grid-cols-3 lg:grid-cols-4 gap-3">
        <MetricCard label="Market Value" value={fmtMoney(result.estimated_market_value)} sub="Estimated" icon={Building2} color="text-primary" highlight />
        <MetricCard label="Market Rent" value={fmtMoney(result.estimated_monthly_rent_market_rate)} sub="Per month" icon={DollarSign} color="text-primary" highlight />
        <MetricCard label="Annual Rent" value={fmtMoney(result.estimated_annual_rent)} sub="Full year" icon={DollarSign} />
        <MetricCard label="Cap Rate" value={fmtPct(result.estimated_cap_rate_pct)} sub="Cap rate" icon={Percent} color={result.estimated_cap_rate_pct > 5 ? 'text-green-600' : 'text-amber-600'} />
        <MetricCard label="Size" value={result.estimated_size_sqft ? `${result.estimated_size_sqft.toLocaleString()} sqft` : 'N/A'} sub="Estimated" icon={Building2} />
        <MetricCard label="Price / Sqft" value={fmtMoney(result.price_per_sqft_sale)} sub="Sale rate" icon={BarChart3} />
        <MetricCard label="Rent / Sqft" value={result.rent_per_sqft_monthly ? `${fmtMoney(result.rent_per_sqft_monthly)}/mo` : 'N/A'} sub="Monthly" icon={DollarSign} />
        <MetricCard label="Buildout Cost" value={fmtMoney(result.restaurant_buildout_cost_estimate)} sub="Restaurant conversion" icon={UtensilsCrossed} />
      </div>
      {Object.keys(roi).length > 0 && (
        <Card>
          <CardHeader className="pb-3"><CardTitle className="text-base" style={{ fontFamily: 'Cormorant Garamond, serif' }}>ROI Scenarios</CardTitle></CardHeader>
          <CardContent>
            <div className="grid grid-cols-3 gap-3">
              {[['conservative','Conservative','bg-blue-50 border-blue-200'],['moderate','Moderate','bg-teal-50 border-teal-200'],['optimistic','Optimistic','bg-green-50 border-green-200']].map(([key,label,cls]) => {
                const s = roi[key] || {};
                return <div key={key} className={`p-3 rounded-xl border text-center ${cls}`}><div className="text-xs font-semibold text-muted-foreground mb-1">{label}</div><div className="text-2xl font-bold tabular-nums" style={{ fontFamily: 'Cormorant Garamond, serif' }}>{fmtPct(s.annual_return_pct)}</div><div className="text-xs text-muted-foreground">Annual Return</div><div className="text-sm font-medium mt-1 tabular-nums">{fmtMoney(s.monthly_noi)}<span className="text-xs">/mo NOI</span></div></div>;
              })}
            </div>
            {result.breakeven_timeline_months && <div className="mt-3 flex items-center gap-2 p-2.5 rounded-lg bg-muted/50"><Clock size={13} className="text-muted-foreground" /><span className="text-sm text-muted-foreground">Break-even: <strong className="text-foreground">{result.breakeven_timeline_months} months</strong></span></div>}
          </CardContent>
        </Card>
      )}
      <div className="grid grid-cols-1 sm:grid-cols-2 gap-4">
        {result.restaurant_viability_notes && <Card className={viability >= 7 ? 'bg-green-50 border-green-200' : viability >= 5 ? 'bg-amber-50 border-amber-200' : 'bg-red-50 border-red-200'}><CardHeader className="pb-2"><CardTitle className="text-sm">Restaurant Viability</CardTitle></CardHeader><CardContent><p className="text-sm">{result.restaurant_viability_notes}</p></CardContent></Card>}
        {result.market_trend && <Card><CardHeader className="pb-2"><CardTitle className="text-sm">Market Trend</CardTitle></CardHeader><CardContent><p className="text-sm text-muted-foreground">{result.market_trend}</p></CardContent></Card>}
      </div>
      {result.investment_summary && <Card className="bg-primary/5 border-primary/20"><CardHeader className="pb-2"><CardTitle className="text-base flex items-center gap-2" style={{ fontFamily: 'Cormorant Garamond, serif' }}><Sparkles size={15} className="text-primary" />Investment Summary</CardTitle></CardHeader><CardContent><p className="text-sm">{result.investment_summary}</p></CardContent></Card>}
      {result.comparable_properties?.length > 0 && <Card><CardHeader className="pb-2"><CardTitle className="text-base" style={{ fontFamily: 'Cormorant Garamond, serif' }}>Comparable Properties</CardTitle></CardHeader><CardContent><div className="space-y-2">{result.comparable_properties.map((p, i) => <div key={i} className="flex items-center justify-between p-2.5 rounded-lg bg-muted/40"><div><div className="text-sm font-medium">{p.address}</div><div className="text-xs text-muted-foreground">{p.notes} · {p.size_sqft?.toLocaleString()} sqft</div></div><div className="text-sm font-semibold text-primary tabular-nums">{fmtMoney(p.rent_per_sqft)}<span className="text-xs text-muted-foreground">/sqft/mo</span></div></div>)}</div></CardContent></Card>}
    </div>
  );
}

/* ══════════════════════════════════════════════
   MAIN CAPITAL HUB PAGE
═══════════════════════════════════════════════ */
export default function CapitalHub() {
  const [activeTab, setActiveTab] = useState('restaurant');

  // Restaurant form
  const [rName, setRName] = useState('');
  const [rVillage, setRVillage] = useState('');
  const [rAddress, setRAddress] = useState('');
  const [rCuisine, setRCuisine] = useState('');
  const [rSeats, setRSeats] = useState('');
  const [rCheck, setRCheck] = useState('');
  const [rAnalysis, setRAnalysis] = useState(null);
  const [rCitations, setRCitations] = useState([]);
  const [rLoading, setRLoading] = useState(false);

  // CO Lookup
  const [coResult, setCoResult] = useState(null);
  const [coCitations, setCoCitations] = useState([]);
  const [coLoading, setCoLoading] = useState(false);
  const [coAddress, setCoAddress] = useState('');
  const [showCO, setShowCO] = useState(false);

  // Real estate form
  const [pName, setPName] = useState('');
  const [pVillage, setPVillage] = useState('');
  const [pAddress, setPAddress] = useState('');
  const [pSize, setPSize] = useState('');
  const [pRent, setPRent] = useState('');
  const [pType, setPType] = useState('');
  const [pAnalysis, setPAnalysis] = useState(null);
  const [pCitations, setPCitations] = useState([]);
  const [pLoading, setPLoading] = useState(false);

  // CRM quick-fill
  const [crmRestaurants, setCrmRestaurants] = useState([]);
  const [crmSpaces, setCrmSpaces] = useState([]);
  const [history, setHistory] = useState([]);
  const [showHistory, setShowHistory] = useState(false);

  useEffect(() => {
    api.get('/api/restaurants?limit=100').then(r => setCrmRestaurants(r.data?.restaurants || [])).catch(() => {});
    api.get('/api/spaces').then(r => setCrmSpaces(r.data || [])).catch(() => {});
    api.get('/api/capital/history?limit=10').then(r => setHistory(r.data || [])).catch(() => {});
  }, []);

  const refreshHistory = () => api.get('/api/capital/history?limit=10').then(r => setHistory(r.data || [])).catch(() => {});

  // Combined: CO lookup → auto-feed seats → capital analysis (all automatic)
  const [rStep, setRStep] = useState('');   // tracks current step label

  const analyzeRestaurant = async () => {
    if (!rName.trim()) { toast.error('Restaurant name required'); return; }
    setRLoading(true); setRAnalysis(null);

    let finalSeats = rSeats ? parseInt(rSeats) : null;
    let coUsed = false;
    const addr = coAddress || rAddress;

    // ── Step 1: Automatic CO lookup if address available ──
    if (addr.trim()) {
      setRStep('Step 1/2 · Searching CO & building records...');
      setCoLoading(true); setShowCO(true); setCoResult(null);
      try {
        const coRes = await api.post('/api/capital/co-lookup', {
          restaurant_name: rName,
          address: addr,
          village: rVillage || null,
        });
        const coData = coRes.data.result;
        setCoResult(coData);
        setCoCitations(coRes.data.citations || []);

        // Auto-use CO seat count when found (override manual entry unless user explicitly set it)
        const coSeats = coData?.estimated_seats;
        if (coSeats && coSeats > 0) {
          finalSeats = coSeats;
          setRSeats(coSeats.toString());
          coUsed = true;
        }
      } catch {
        // CO lookup failed silently — proceed with manual data
      } finally {
        setCoLoading(false);
      }
    }

    // ── Step 2: Capital analysis using CO-verified seat count ──
    setRStep(`Step ${addr.trim() ? '2' : '1'}/2 · Running capital analysis${coUsed ? ` with ${finalSeats} CO-verified seats` : ''}...`);
    try {
      const res = await api.post('/api/capital/restaurant', {
        name: rName,
        village: rVillage || null,
        address: addr || null,
        cuisine: rCuisine || null,
        seat_count: finalSeats,
        avg_check: rCheck ? parseFloat(rCheck) : null,
      });
      setRAnalysis({ ...res.data.analysis, co_verified_seats: coUsed ? finalSeats : null });
      setRCitations(res.data.citations || []);
      toast.success(coUsed
        ? `Analysis complete using ${finalSeats} CO-verified seats!`
        : 'Capital analysis complete!');
      refreshHistory();
    } catch (err) {
      toast.error(err.response?.data?.detail || 'Analysis failed');
    } finally {
      setRLoading(false);
      setRStep('');
    }
  };

  const lookupCO = async () => {
    const addr = coAddress || rAddress;
    if (!rName.trim() || !addr.trim()) { toast.error('Restaurant name and address are required'); return; }
    setCoLoading(true); setCoResult(null); setShowCO(true);
    try {
      const res = await api.post('/api/capital/co-lookup', {
        restaurant_name: rName, address: addr, village: rVillage || null,
      });
      setCoResult(res.data.result); setCoCitations(res.data.citations || []);
      const seats = res.data.result?.estimated_seats;
      if (seats && seats > 0) {
        toast.success(`CO lookup complete — ${seats} estimated seats found`);
      } else {
        toast.info('CO lookup complete — see results below');
      }
    } catch (err) { toast.error(err.response?.data?.detail || 'CO lookup failed'); }
    finally { setCoLoading(false); }
  };

  const useSeatCountFromCO = (seats) => {
    setRSeats(seats.toString());
    toast.success(`Seat count set to ${seats} from CO record`);
    setShowCO(false);
  };

  const analyzeProperty = async () => {
    if (!pName.trim()) { toast.error('Property name required'); return; }
    setPLoading(true); setPAnalysis(null);
    try {
      const res = await api.post('/api/capital/realestate', {
        name: pName, village: pVillage || null, address: pAddress || null,
        size_sqft: pSize ? parseInt(pSize) : null, rent_monthly: pRent ? parseFloat(pRent) : null, space_type: pType || null,
      });
      setPAnalysis(res.data.analysis); setPCitations(res.data.citations || []);
      toast.success('Property analysis complete!');
      refreshHistory();
    } catch (err) { toast.error(err.response?.data?.detail || 'Analysis failed'); }
    finally { setPLoading(false); }
  };

  const fillFromCrm = (r) => { setRName(r.name||''); setRVillage(r.village||''); setRAddress(r.address||''); setRCuisine(r.cuisine||''); setRSeats(r.seat_count?.toString()||''); setRCheck(r.avg_check?.toString()||''); toast.success(`Loaded ${r.name}`); };
  const fillSpaceFromCrm = (s) => { setPName(s.name||''); setPVillage(s.village||''); setPAddress(s.address||''); setPSize(s.size_sqft?.toString()||''); setPRent(s.rent_monthly?.toString()||''); setPType(s.space_type||''); toast.success(`Loaded ${s.name}`); };

  return (
    <div className="space-y-5">
      {/* Header */}
      <div className="flex items-start justify-between">
        <div>
          <h1 className="page-title flex items-center gap-2">
            <TrendingUp size={26} className="text-primary" />
            Capital Hub
          </h1>
          <p className="page-subtitle">
            Live financial intelligence · <span className="font-semibold text-primary">Perplexity sonar-pro</span> · Certificate of Occupancy scraper
          </p>
        </div>
        <Button variant="outline" size="sm" onClick={() => setShowHistory(!showHistory)} className="flex items-center gap-1.5">
          <History size={13} /> History ({history.length})
        </Button>
      </div>

      {/* History */}
      {showHistory && history.length > 0 && (
        <Card>
          <CardHeader className="pb-2"><CardTitle className="text-sm">Recent Analyses</CardTitle></CardHeader>
          <CardContent>
            <div className="space-y-1.5">
              {history.map((h, i) => (
                <div key={i} className="flex items-center justify-between p-2 rounded-lg bg-muted/40 hover:bg-muted cursor-pointer"
                  onClick={() => {
                    if (h.type === 'restaurant') { setRAnalysis(h.result); setRCitations(h.citations||[]); setActiveTab('restaurant'); }
                    else if (h.type === 'realestate') { setPAnalysis(h.result); setPCitations(h.citations||[]); setActiveTab('realestate'); }
                    setShowHistory(false);
                  }}>
                  <div className="flex items-center gap-2">
                    <Badge variant="secondary" className="text-xs">{h.type === 'restaurant' ? '🍽️' : h.type === 'co_lookup' ? '📋' : '🏢'}</Badge>
                    <span className="text-sm font-medium">{h.restaurant_name || h.query?.name}</span>
                    <span className="text-xs text-muted-foreground">{h.query?.village}</span>
                  </div>
                  <span className="text-xs text-muted-foreground">{fmtDate(h.created_at)}</span>
                </div>
              ))}
            </div>
          </CardContent>
        </Card>
      )}

      <div className="flex items-start gap-2.5 px-4 py-2.5 rounded-xl bg-amber-50 border border-amber-200">
        <AlertTriangle size={14} className="text-amber-600 mt-0.5 flex-shrink-0" />
        <p className="text-xs text-amber-700"><strong>Estimates only</strong> — All figures are based on publicly available data and industry benchmarks. Use as intelligence for relationship conversations, not as verified financial statements.</p>
      </div>

      <Tabs value={activeTab} onValueChange={setActiveTab}>
        <TabsList className="mb-4">
          <TabsTrigger value="restaurant"><UtensilsCrossed size={13} className="mr-1.5" />Restaurant Capital</TabsTrigger>
          <TabsTrigger value="realestate"><Building2 size={13} className="mr-1.5" />Real Estate Capital</TabsTrigger>
        </TabsList>

        {/* ══ RESTAURANT CAPITAL ══ */}
        <TabsContent value="restaurant">
          <div className="grid grid-cols-1 lg:grid-cols-3 gap-5">
            {/* Left: form */}
            <div className="space-y-4">
              <Card>
                <CardHeader className="pb-3">
                  <CardTitle className="text-base" style={{ fontFamily: 'Cormorant Garamond, serif' }}>Restaurant Analysis</CardTitle>
                  <p className="text-xs text-muted-foreground">Searches menus, reviews, reservations, news & CO records</p>
                </CardHeader>
                <CardContent className="space-y-3">
                  <div className="space-y-1.5"><Label>Restaurant Name *</Label><Input value={rName} onChange={e => setRName(e.target.value)} placeholder="Mavericks Montauk" data-testid="capital-restaurant-name" /></div>
                  <div className="space-y-1.5"><Label>Village</Label>
                    <Select value={rVillage} onValueChange={setRVillage}>
                      <SelectTrigger><SelectValue placeholder="Select village" /></SelectTrigger>
                      <SelectContent>{VILLAGES.map(v => <SelectItem key={v} value={v}>{v}</SelectItem>)}</SelectContent>
                    </Select>
                  </div>
                  <div className="space-y-1.5"><Label>Address</Label><Input value={rAddress} onChange={e => setRAddress(e.target.value)} placeholder="123 Main St" /></div>
                  <div className="space-y-1.5"><Label>Cuisine Type</Label><Input value={rCuisine} onChange={e => setRCuisine(e.target.value)} placeholder="American / Seafood..." /></div>
                  <div className="grid grid-cols-2 gap-2">
                    <div className="space-y-1.5"><Label>Seats {rSeats ? <span className="text-primary font-semibold">(CO: {rSeats})</span> : '(optional)'}</Label><Input type="number" value={rSeats} onChange={e => setRSeats(e.target.value)} placeholder="80" /></div>
                    <div className="space-y-1.5"><Label>Avg Check</Label><Input type="number" value={rCheck} onChange={e => setRCheck(e.target.value)} placeholder="75" /></div>
                  </div>
                  <Button onClick={analyzeRestaurant} disabled={rLoading || !rName.trim()} className="w-full bg-primary" data-testid="analyze-restaurant-btn">
                    {rLoading
                      ? <span className="flex items-center gap-2"><div className="w-4 h-4 border-2 border-primary-foreground border-t-transparent rounded-full animate-spin" /><span className="text-xs truncate">{rStep || 'Analyzing...'}</span></span>
                      : <span className="flex items-center gap-2"><Search size={14} />{(rAddress || coAddress) ? 'Analyze Capital (with CO lookup)' : 'Analyze Capital'}</span>}
                  </Button>
                  {(rAddress || coAddress) && !rLoading && (
                    <p className="text-xs text-primary text-center flex items-center justify-center gap-1">
                      <Shield size={10} className="text-[#C9A46A]" />
                      Will auto-search CO records &amp; use verified seat count
                    </p>
                  )}

                  <Separator />

                  {/* CO Lookup */}
                  <div className="space-y-2">
                    <div className="flex items-center gap-2">
                      <Shield size={13} className="text-[#C9A46A]" />
                      <Label className="text-xs font-semibold uppercase tracking-wide">Certificate of Occupancy</Label>
                    </div>
                    <Input value={coAddress} onChange={e => setCoAddress(e.target.value)} placeholder={rAddress || "Full street address for CO lookup"} />
                    <Button onClick={lookupCO} disabled={coLoading || !rName.trim()} variant="outline" className="w-full border-[#C9A46A]/40 hover:bg-[#C9A46A]/10" data-testid="co-lookup-btn">
                      {coLoading ? <span className="flex items-center gap-2"><div className="w-4 h-4 border-2 border-muted-foreground border-t-transparent rounded-full animate-spin" />Searching records...</span>
                        : <span className="flex items-center gap-2"><FileText size={13} />Look Up CO & Seat Count</span>}
                    </Button>
                    <p className="text-xs text-muted-foreground text-center">Searches Suffolk County & town building dept records</p>
                  </div>
                </CardContent>
              </Card>

              {/* CRM quick-fill */}
              {crmRestaurants.length > 0 && (
                <Card>
                  <CardHeader className="pb-2"><CardTitle className="text-sm">Quick Fill from CRM</CardTitle></CardHeader>
                  <CardContent>
                    <ScrollArea className="h-44">
                      <div className="space-y-1">
                        {crmRestaurants.slice(0, 30).map(r => (
                          <button key={r.id} onClick={() => fillFromCrm(r)} className="w-full flex items-center justify-between p-2 rounded-lg hover:bg-muted text-left">
                            <div><div className="text-xs font-medium">{r.name}</div><div className="text-xs text-muted-foreground">{r.village}</div></div>
                            <ChevronRight size={12} className="text-muted-foreground" />
                          </button>
                        ))}
                      </div>
                    </ScrollArea>
                  </CardContent>
                </Card>
              )}
            </div>

            {/* Right: results */}
            <div className="lg:col-span-2 space-y-5">
              {/* CO Results */}
              {showCO && (
                <div>
                  <div className="flex items-center gap-2 mb-3">
                    <Shield size={16} className="text-[#C9A46A]" />
                    <h3 className="font-semibold text-sm" style={{ fontFamily: 'Cormorant Garamond, serif' }}>Certificate of Occupancy Results</h3>
                    <button onClick={() => setShowCO(false)} className="ml-auto text-xs text-muted-foreground hover:text-foreground">Dismiss</button>
                  </div>
                  {coLoading ? (
                    <Card><CardContent className="py-10 text-center space-y-3">
                      <div className="w-7 h-7 border-2 border-[#C9A46A] border-t-transparent rounded-full animate-spin mx-auto" />
                      <p className="text-sm text-muted-foreground">Searching Suffolk County & town building records...</p>
                    </CardContent></Card>
                  ) : coResult ? (
                    <CertificateOfOccupancy result={coResult} citations={coCitations} onUseSeatCount={useSeatCountFromCO} />
                  ) : null}
                  {showCO && !coLoading && <Separator className="my-4" />}
                </div>
              )}

              {/* Capital Analysis Results */}
              {rLoading && !rStep.includes('Step 1') ? (
                <Card><CardContent className="py-16 text-center space-y-3">
                  <div className="w-8 h-8 border-2 border-primary border-t-transparent rounded-full animate-spin mx-auto" />
                  <div>
                    <p className="text-sm font-medium">{rStep || `Analyzing ${rName}...`}</p>
                    <p className="text-xs text-muted-foreground mt-1">Searching menus, reviews, reservations & benchmarks</p>
                  </div>
                </CardContent></Card>
              ) : !rLoading && rAnalysis ? (
                <RestaurantAnalysis result={rAnalysis} citations={rCitations} />
              ) : !showCO ? (
                <Card><CardContent className="py-16 text-center">
                  <TrendingUp size={48} className="mx-auto mb-4 text-muted-foreground/20" />
                  <h3 className="text-lg font-semibold" style={{ fontFamily: 'Cormorant Garamond, serif' }}>Restaurant Capital Intelligence</h3>
                  <p className="text-sm text-muted-foreground mt-2 max-w-sm mx-auto">Enter a restaurant name and optionally look up its Certificate of Occupancy for accurate seat counts, then run a full capital analysis.</p>
                </CardContent></Card>
              ) : null}
            </div>
          </div>
        </TabsContent>

        {/* ══ REAL ESTATE CAPITAL ══ */}
        <TabsContent value="realestate">
          <div className="grid grid-cols-1 lg:grid-cols-3 gap-5">
            <div className="space-y-4">
              <Card>
                <CardHeader className="pb-3">
                  <CardTitle className="text-base" style={{ fontFamily: 'Cormorant Garamond, serif' }}>Property Analysis</CardTitle>
                  <p className="text-xs text-muted-foreground">Searches LoopNet, Crexi, CoStar & Suffolk County records</p>
                </CardHeader>
                <CardContent className="space-y-3">
                  <div className="space-y-1.5"><Label>Property Name / Address *</Label><Input value={pName} onChange={e => setPName(e.target.value)} placeholder="Main St Storefront, Sag Harbor" data-testid="capital-property-name" /></div>
                  <div className="space-y-1.5"><Label>Village</Label>
                    <Select value={pVillage} onValueChange={setPVillage}>
                      <SelectTrigger><SelectValue placeholder="Select village" /></SelectTrigger>
                      <SelectContent>{VILLAGES.map(v => <SelectItem key={v} value={v}>{v}</SelectItem>)}</SelectContent>
                    </Select>
                  </div>
                  <div className="space-y-1.5"><Label>Full Address</Label><Input value={pAddress} onChange={e => setPAddress(e.target.value)} placeholder="123 Main St" /></div>
                  <div className="space-y-1.5"><Label>Space Type</Label>
                    <Select value={pType} onValueChange={setPType}>
                      <SelectTrigger><SelectValue placeholder="Select type" /></SelectTrigger>
                      <SelectContent>
                        <SelectItem value="Restaurant-Ready">Restaurant-Ready</SelectItem>
                        <SelectItem value="Raw Space">Raw Space</SelectItem>
                        <SelectItem value="Retail">Retail</SelectItem>
                        <SelectItem value="Mixed Use">Mixed Use</SelectItem>
                        <SelectItem value="Waterfront">Waterfront</SelectItem>
                      </SelectContent>
                    </Select>
                  </div>
                  <div className="grid grid-cols-2 gap-2">
                    <div className="space-y-1.5"><Label>Size (sqft)</Label><Input type="number" value={pSize} onChange={e => setPSize(e.target.value)} placeholder="2000" /></div>
                    <div className="space-y-1.5"><Label>Asking Rent/mo</Label><Input type="number" value={pRent} onChange={e => setPRent(e.target.value)} placeholder="8000" /></div>
                  </div>
                  <Button onClick={analyzeProperty} disabled={pLoading || !pName.trim()} className="w-full bg-primary" data-testid="analyze-property-btn">
                    {pLoading ? <span className="flex items-center gap-2"><div className="w-4 h-4 border-2 border-primary-foreground border-t-transparent rounded-full animate-spin" />Searching web...</span>
                      : <span className="flex items-center gap-2"><Search size={14} />Analyze Capital</span>}
                  </Button>
                </CardContent>
              </Card>
              {crmSpaces.length > 0 && (
                <Card>
                  <CardHeader className="pb-2"><CardTitle className="text-sm">Quick Fill from CRM</CardTitle></CardHeader>
                  <CardContent>
                    <ScrollArea className="h-44">
                      <div className="space-y-1">
                        {crmSpaces.slice(0, 20).map(s => (
                          <button key={s.id} onClick={() => fillSpaceFromCrm(s)} className="w-full flex items-center justify-between p-2 rounded-lg hover:bg-muted text-left">
                            <div><div className="text-xs font-medium">{s.name}</div><div className="text-xs text-muted-foreground">{s.village}{s.size_sqft ? ` · ${s.size_sqft.toLocaleString()} sqft` : ''}</div></div>
                            <ChevronRight size={12} className="text-muted-foreground" />
                          </button>
                        ))}
                      </div>
                    </ScrollArea>
                  </CardContent>
                </Card>
              )}
            </div>
            <div className="lg:col-span-2">
              {pLoading ? (
                <Card><CardContent className="py-16 text-center space-y-3">
                  <div className="w-8 h-8 border-2 border-primary border-t-transparent rounded-full animate-spin mx-auto" />
                  <p className="text-sm font-medium">Perplexity is researching {pName}...</p>
                  <p className="text-xs text-muted-foreground">Searching LoopNet, Crexi, CoStar, Suffolk County records</p>
                </CardContent></Card>
              ) : pAnalysis ? (
                <RealEstateAnalysis result={pAnalysis} citations={pCitations} />
              ) : (
                <Card><CardContent className="py-16 text-center">
                  <Building2 size={48} className="mx-auto mb-4 text-muted-foreground/20" />
                  <h3 className="text-lg font-semibold" style={{ fontFamily: 'Cormorant Garamond, serif' }}>Real Estate Capital Intelligence</h3>
                  <p className="text-sm text-muted-foreground mt-2 max-w-sm mx-auto">Enter a property address to get market value, cap rate, ROI scenarios, and restaurant viability score.</p>
                </CardContent></Card>
              )}
            </div>
          </div>
        </TabsContent>
      </Tabs>
    </div>
  );
}
